import { ApplicationRef, inject, Injectable, signal } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { concat, filter, first, interval } from 'rxjs';
import { WINDOW } from '../../injectors/window';

/** How often to poll the server for a fresh build once the app is stable. */
const CHECK_INTERVAL_MS = 30 * 60 * 1000;

/**
 * Detects when the service worker has fetched a newer build and exposes it as a
 * signal for the update banner.
 *
 * PWA caching can otherwise keep a long-lived tab on a stale version until the
 * user hard-refreshes, so we also poll periodically instead of relying only on
 * the check done at registration.
 */
@Injectable({ providedIn: 'root' })
export class VersionUpdateService {
  private readonly swUpdate = inject(SwUpdate);
  private readonly appRef = inject(ApplicationRef);
  private readonly window = inject(WINDOW);

  /** True once a new version is downloaded and ready to activate. */
  readonly updateReady = signal(false);

  init(): void {
    // Disabled in dev and on the server (SSR) - nothing to detect there.
    if (!this.swUpdate.isEnabled) return;

    this.swUpdate.versionUpdates
      .pipe(filter((e): e is VersionReadyEvent => e.type === 'VERSION_READY'))
      .subscribe(() => this.updateReady.set(true));

    // Poll only once the app has stabilized so the initial load isn't slowed,
    // then on a fixed interval to catch versions shipped mid-session.
    const stable$ = this.appRef.isStable.pipe(first((stable) => stable));
    concat(stable$, interval(CHECK_INTERVAL_MS)).subscribe(() => {
      void this.swUpdate.checkForUpdate();
    });
  }

  /** Activate the downloaded version and reload the page to run it. */
  async reload(): Promise<void> {
    await this.swUpdate.activateUpdate();
    this.window.location.reload();
  }
}
